import {computed, Directive, effect, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild} from '@angular/core';
import {BasicTaDetails, FullTaDetails} from '../../api';
import {TaAppStore} from '../../state/clcta-store';
import {TaSubmissionDatasource} from '../../datasources/ta-submission.datasource';
import {Router} from '@angular/router';
import {LoadingService} from '../../services/loading.service';
import {TaSearchDatasource} from '../../datasources/ta-search-datasource';
import {PermissionService} from '../../services/permission.service';
import {toTAStatusText} from '../../util/app.types';
import {TaNavigationService} from '../../services/ta-navigation.service';
import {fuzzyMatcher} from '../../util/search.utils';

@Directive()
export abstract class TaListBase implements OnInit, OnDestroy {
  store = inject(TaAppStore);
  taSubmissionDatasource = inject(TaSubmissionDatasource);
  taSearchDatasource = inject(TaSearchDatasource);
  router = inject(Router);
  loadingService = inject(LoadingService);
  host = inject(ElementRef);
  permissionService = inject(PermissionService);
  taNavigationService = inject(TaNavigationService);
  @ViewChild("loadingRef") loadingRef!: ElementRef;

  searchStr = signal<string>('');
  availableTaList = signal<BasicTaDetails[]>([]);
  filteredTaList = computed(() => {
    const matches = fuzzyMatcher(this.searchStr());
    return this.availableTaList().filter(taDetails => matches(
      [taDetails.taNumber, taDetails.projectCode],
      [taDetails.mainDestinationLocationName, taDetails.primaryTraveller?.details?.name,
        taDetails.status && toTAStatusText(taDetails.status)],
    ));
  });
  selectedTa = computed(() => this.store.taSubmission());

  // Rows ticked for a batch action. Only lists that render checkboxes use these.
  private selectedIds = signal<ReadonlySet<string>>(new Set());
  selectedTaList = computed(() =>
    this.availableTaList().filter(taDetails => !!taDetails.id && this.selectedIds().has(taDetails.id)));
  selectedCount = computed(() => this.selectedTaList().length);
  // The header checkbox reflects the rows currently displayed, which is what it ticks.
  allFilteredSelected = computed(() => {
    const rows = this.filteredTaList();
    return rows.length > 0 && rows.every(taDetails => !!taDetails.id && this.selectedIds().has(taDetails.id));
  });

  constructor() {
    // Replacing the list drops the selection, so an action that reloads always leaves it cleared
    // and a stale id can never be actioned twice.
    effect(() => {
      this.availableTaList();
      this.clearSelection();
    });
  }

  // Lifecycle hooks
  ngOnInit(): void {
    this.store.resetTaState();
    this.searchStr.set('');
    this.availableTaList.set([]);
  }

  ngOnDestroy(): void {
    this.searchStr.set('');
    this.availableTaList.set([]);
  }

  viewTaSubmission(taSubmission: FullTaDetails | BasicTaDetails): void {
    this.taNavigationService.open(taSubmission);
  }

  clearSearchStr(): void {
    this.searchStr.set('');
  }

  setSelectedTa(taSubmission: BasicTaDetails) {
    if (!!taSubmission.id) {
      this.taSubmissionDatasource.loadServerTaSubmission(taSubmission.id);
    }
  }

  clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  isSelected(taDetails: BasicTaDetails): boolean {
    return !!taDetails.id && this.selectedIds().has(taDetails.id);
  }

  setSelected(taDetails: BasicTaDetails, checked: boolean): void {
    if (!taDetails.id) {
      return;
    }
    this.selectedIds.update(ids => {
      const next = new Set(ids);
      checked ? next.add(taDetails.id!) : next.delete(taDetails.id!);
      return next;
    });
  }

  setAllFilteredSelected(checked: boolean): void {
    this.selectedIds.update(ids => {
      const next = new Set(ids);
      for (const taDetails of this.filteredTaList()) {
        if (taDetails.id) {
          checked ? next.add(taDetails.id) : next.delete(taDetails.id);
        }
      }
      return next;
    });
  }
}
